// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM chrome: the top bar (status, SAS, presence, invite), the floating drawing
 * toolbar, the board mount point + live-cursor layer, the event-log drawer, and
 * the footer. Pure presentation — it emits callbacks and exposes setters; all the
 * drawing, CRDT and network logic lives elsewhere.
 */
import { icon } from './icons.ts';
import { openModal, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { mountEventDrawer } from './eventlog.ts';
import { logicalToScreen, type Fit } from './draw.ts';
import { fmtHere } from './format.ts';
import type { Cursor, Member, Status, Tool } from './types.ts';

export interface UIHandlers {
  onToolChange: (t: Tool) => void;
  onColorChange: (c: string) => void;
  onWidthChange: (w: number) => void;
  onUndo: () => void;
  onClear: () => void;
  onExample: () => void;
  onExportPng: () => void;
  onExportSvg: () => void;
  onCopyLink: () => void;
  onShareLink: () => void;
  onToggleQr: () => void;
  onNameChange: (name: string) => void;
  onLeave: () => void;
}

export const PALETTE = ['#1f2430', '#64748b', '#e5484d', '#f76b15', '#f5c518', '#30a46c', '#3b82f6', '#8b5cf6', '#e93d82'];
export const WIDTHS = [2, 4, 8, 16];

const TOOLS: Array<{ tool: Tool; ic: string; label: string; key: string }> = [
  { tool: 'pen', ic: 'pen', label: 'Pen', key: 'P' },
  { tool: 'line', ic: 'line', label: 'Line', key: 'L' },
  { tool: 'arrow', ic: 'arrowTool', label: 'Arrow', key: 'A' },
  { tool: 'rect', ic: 'square', label: 'Rectangle', key: 'R' },
  { tool: 'ellipse', ic: 'circle', label: 'Ellipse', key: 'O' },
  { tool: 'text', ic: 'type', label: 'Text', key: 'T' },
  { tool: 'eraser', ic: 'eraser', label: 'Eraser', key: 'E' },
];

export class AppUI {
  private boardWrapEl!: HTMLElement;
  private cursorLayer!: HTMLElement;
  private sasEl!: HTMLElement;
  private hereEl!: HTMLElement;
  private statusDot!: HTMLElement;
  private membersEl!: HTMLElement;
  private nameInput!: HTMLInputElement;
  private invitePop!: HTMLElement;
  private inviteInput!: HTMLInputElement;
  private qrBox!: HTMLElement;
  private drawerEl!: HTMLElement;
  private toolBtns = new Map<Tool, HTMLElement>();
  private colorBtns = new Map<string, HTMLElement>();
  private widthBtns = new Map<number, HTMLElement>();
  private cursorEls = new Map<string, HTMLElement>();

  constructor(
    private root: HTMLElement,
    private h: UIHandlers,
  ) {
    this.build();
  }

  private build(): void {
    this.root.innerHTML = `
      <header class="topbar">
        <div class="brand">
          <span class="brand-mark">${icon('pen')}</span>
          <span class="brand-name">Sketchwell</span>
        </div>
        <div class="room-info">
          <span class="status"><span class="status-dot" id="status-dot"></span><span id="here">connecting…</span></span>
          <button type="button" class="sas" id="sas" title="Everyone in the room should see the same four words">
            <span class="sas-label">verify</span><span class="sas-words">····</span>
          </button>
          <div class="members" id="members"></div>
        </div>
        <div class="top-actions">
          <input id="name" class="name-input" type="text" maxlength="24" aria-label="Your display name" placeholder="Your name" />
          <button type="button" class="btn btn-accent" id="invite-btn">${icon('link')}<span>Invite</span></button>
          <button type="button" class="icon-btn" id="help-btn" title="How it works" aria-label="How it works">${icon('help')}</button>
          <button type="button" class="icon-btn" id="privacy-btn" title="Privacy" aria-label="Privacy">${icon('shield')}</button>
          <button type="button" class="icon-btn" id="about-btn" title="About" aria-label="About">${icon('info')}</button>
          <button type="button" class="icon-btn" id="log-btn" title="Event log" aria-label="Event log">${icon('logDrawer')}</button>
        </div>
        <div class="invite-pop" id="invite-pop" hidden>
          <div class="invite-row">
            <input type="text" id="invite-link" readonly aria-label="Invite link" />
            <button type="button" class="icon-btn" id="copy-link" title="Copy link" aria-label="Copy link">${icon('copy')}</button>
          </div>
          <div class="invite-actions">
            <button type="button" class="btn" id="share-link">${icon('share')}<span>Share</span></button>
            <button type="button" class="btn" id="qr-btn">${icon('qr')}<span>QR code</span></button>
          </div>
          <div class="qr-box" id="qr-box" hidden></div>
          <p class="invite-note">Anyone with this link can draw on the board. The passphrase rides in the link and never touches a server.</p>
        </div>
      </header>

      <button type="button" class="trust-banner" id="trust-banner">
        ${icon('shield')}<span>Runs entirely in your browser. Your drawing goes peer-to-peer, encrypted — never to a server.</span>
      </button>

      <main class="main-content">
        <div class="board-wrap" id="board-wrap">
          <div class="cursor-layer" id="cursor-layer"></div>
        </div>

        <div class="toolbar" role="toolbar" aria-label="Drawing tools">
          <div class="tool-group" id="tools"></div>
          <div class="tool-sep"></div>
          <div class="tool-group swatches" id="swatches"></div>
          <div class="tool-sep"></div>
          <div class="tool-group widths" id="widths"></div>
          <div class="tool-sep"></div>
          <div class="tool-group">
            <button type="button" class="tool-btn" id="undo-btn" title="Undo (Cmd/Ctrl+Z)" aria-label="Undo">${icon('undo')}</button>
            <button type="button" class="tool-btn" id="clear-btn" title="Clear board" aria-label="Clear board">${icon('trash')}</button>
            <button type="button" class="tool-btn" id="example-btn" title="Add an example doodle" aria-label="Add an example doodle">${icon('sparkles')}</button>
          </div>
          <div class="tool-sep"></div>
          <div class="tool-group">
            <button type="button" class="btn btn-ghost" id="png-btn" title="Export PNG">${icon('download')}<span>PNG</span></button>
            <button type="button" class="btn btn-ghost" id="svg-btn" title="Export SVG">${icon('image')}<span>SVG</span></button>
          </div>
        </div>
      </main>

      <footer class="site-footer">
        <div class="footer-inner">
          <span>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
          <button type="button" class="link-btn" id="leave-btn">Leave &amp; start fresh</button>
        </div>
      </footer>

      <aside class="drawer" id="drawer" hidden aria-label="Event log"></aside>
    `;

    this.boardWrapEl = this.q('#board-wrap');
    this.cursorLayer = this.q('#cursor-layer');
    this.sasEl = this.q('#sas .sas-words');
    this.hereEl = this.q('#here');
    this.statusDot = this.q('#status-dot');
    this.membersEl = this.q('#members');
    this.nameInput = this.q('#name');
    this.invitePop = this.q('#invite-pop');
    this.inviteInput = this.q('#invite-link');
    this.qrBox = this.q('#qr-box');
    this.drawerEl = this.q('#drawer');

    this.buildTools();
    this.buildSwatches();
    this.buildWidths();
    this.wire();
    mountEventDrawer(this.drawerEl);
    this.q('#drawer').addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#drawer-close')) this.closeDrawer();
    });
  }

  private buildTools(): void {
    const host = this.q('#tools');
    for (const t of TOOLS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tool-btn';
      b.title = `${t.label} (${t.key})`;
      b.setAttribute('aria-label', t.label);
      b.innerHTML = icon(t.ic);
      b.addEventListener('click', () => this.h.onToolChange(t.tool));
      this.toolBtns.set(t.tool, b);
      host.appendChild(b);
    }
  }

  private buildSwatches(): void {
    const host = this.q('#swatches');
    for (const c of PALETTE) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'swatch';
      b.style.setProperty('--sw', c);
      b.title = c;
      b.setAttribute('aria-label', `Colour ${c}`);
      b.addEventListener('click', () => this.h.onColorChange(c));
      this.colorBtns.set(c, b);
      host.appendChild(b);
    }
  }

  private buildWidths(): void {
    const host = this.q('#widths');
    for (const w of WIDTHS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'width-btn';
      b.title = `Width ${w}`;
      b.setAttribute('aria-label', `Stroke width ${w}`);
      b.innerHTML = `<span class="width-dot" style="width:${Math.min(20, w + 3)}px;height:${Math.min(20, w + 3)}px"></span>`;
      b.addEventListener('click', () => this.h.onWidthChange(w));
      this.widthBtns.set(w, b);
      host.appendChild(b);
    }
  }

  private wire(): void {
    this.q('#help-btn').addEventListener('click', () => openModal('How Sketchwell works', howItWorksHtml()));
    this.q('#privacy-btn').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    this.q('#about-btn').addEventListener('click', () => openModal('About Sketchwell', aboutHtml()));
    this.q('#trust-banner').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    this.q('#log-btn').addEventListener('click', () => this.toggleDrawer());

    this.q('#invite-btn').addEventListener('click', () => this.toggleInvite());
    this.q('#copy-link').addEventListener('click', () => this.h.onCopyLink());
    this.q('#share-link').addEventListener('click', () => this.h.onShareLink());
    this.q('#qr-btn').addEventListener('click', () => this.h.onToggleQr());
    this.q('#sas').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));

    this.q('#undo-btn').addEventListener('click', () => this.h.onUndo());
    this.q('#clear-btn').addEventListener('click', () => this.h.onClear());
    this.q('#example-btn').addEventListener('click', () => this.h.onExample());
    this.q('#png-btn').addEventListener('click', () => this.h.onExportPng());
    this.q('#svg-btn').addEventListener('click', () => this.h.onExportSvg());
    this.q('#leave-btn').addEventListener('click', () => this.h.onLeave());

    this.nameInput.addEventListener('change', () => this.h.onNameChange(this.nameInput.value));
    this.nameInput.addEventListener('blur', () => this.h.onNameChange(this.nameInput.value));

    // Close the invite popover on an outside click.
    document.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (this.invitePop.hidden) return;
      if (t.closest('#invite-pop') || t.closest('#invite-btn')) return;
      this.invitePop.hidden = true;
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (!this.invitePop.hidden) this.invitePop.hidden = true;
        this.closeDrawer();
      }
    });
  }

  boardWrap(): HTMLElement {
    return this.boardWrapEl;
  }

  // ── status / presence ─────────────────────────────────────────────────────
  setStatus(status: Status, count: number): void {
    this.statusDot.dataset.status = status;
    const label =
      status === 'connecting' ? 'connecting…' : status === 'waiting' ? 'waiting for others' : status === 'error' ? 'connection error' : fmtHere(count);
    this.hereEl.textContent = label;
  }

  setSas(words: string[]): void {
    this.sasEl.textContent = words.join(' ');
  }

  renderMembers(list: Member[], selfId: string): void {
    this.membersEl.innerHTML = '';
    for (const m of list.slice(0, 6)) {
      const el = document.createElement('span');
      el.className = 'avatar';
      el.style.setProperty('--hue', String(m.hue));
      el.textContent = (m.name || '?').slice(0, 1).toUpperCase();
      el.title = m.id === selfId ? `${m.name} (you)` : m.name;
      this.membersEl.appendChild(el);
    }
    if (list.length > 6) {
      const more = document.createElement('span');
      more.className = 'avatar more';
      more.textContent = `+${list.length - 6}`;
      this.membersEl.appendChild(more);
    }
  }

  setName(name: string): void {
    if (this.nameInput.value !== name) this.nameInput.value = name;
  }

  // ── invite ────────────────────────────────────────────────────────────────
  private toggleInvite(): void {
    this.invitePop.hidden = !this.invitePop.hidden;
  }
  setInviteLink(url: string): void {
    this.inviteInput.value = url;
  }
  setQr(dataUrl: string): void {
    this.qrBox.innerHTML = `<img src="${dataUrl}" alt="QR code for the invite link" width="200" height="200" />`;
  }
  toggleQr(): void {
    this.qrBox.hidden = !this.qrBox.hidden;
  }

  // ── active tool/colour/width reflection ────────────────────────────────────
  setActiveTool(t: Tool): void {
    for (const [tool, el] of this.toolBtns) el.classList.toggle('active', tool === t);
  }
  setActiveColor(c: string): void {
    for (const [col, el] of this.colorBtns) el.classList.toggle('active', col === c);
  }
  setActiveWidth(w: number): void {
    for (const [width, el] of this.widthBtns) el.classList.toggle('active', width === w);
  }

  // ── live cursors ───────────────────────────────────────────────────────────
  renderCursors(cursors: Map<string, Cursor>, members: Map<string, Member>, fit: Fit): void {
    const seen = new Set<string>();
    for (const [id, c] of cursors) {
      seen.add(id);
      const m = members.get(id);
      let el = this.cursorEls.get(id);
      if (!el) {
        el = document.createElement('div');
        el.className = 'remote-cursor';
        el.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 3l6 16 2-6 6-2z"/></svg><span class="cursor-name"></span>`;
        this.cursorLayer.appendChild(el);
        this.cursorEls.set(id, el);
      }
      const hue = m?.hue ?? 210;
      el.style.setProperty('--hue', String(hue));
      (el.querySelector('.cursor-name') as HTMLElement).textContent = m?.name || 'Guest';
      const s = logicalToScreen(c.x, c.y, fit);
      el.style.transform = `translate(${s.x}px, ${s.y}px)`;
    }
    for (const [id, el] of this.cursorEls) {
      if (!seen.has(id)) {
        el.remove();
        this.cursorEls.delete(id);
      }
    }
  }

  // ── drawer ─────────────────────────────────────────────────────────────────
  private toggleDrawer(): void {
    if (this.drawerEl.hidden) this.openDrawer();
    else this.closeDrawer();
  }
  private openDrawer(): void {
    this.drawerEl.hidden = false;
    this.drawerEl.classList.add('open');
  }
  private closeDrawer(): void {
    this.drawerEl.classList.remove('open');
    this.drawerEl.hidden = true;
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.root.querySelector(sel) as T;
  }
}
